import type * as React from "react";
import type { PresetOption } from "@/types/createAgent";
import { isRefinementPreset } from "@/lib/agent/presetPresentation";
import {
  getColorHexesForSwatch,
  getColorHexesForValue,
} from "@/utils/colorResolution";

type SilhouetteKind =
  | "ball_gown"
  | "a_line"
  | "mermaid"
  | "trumpet"
  | "fit_flare"
  | "empire"
  | "sheath"
  | "fitted"
  | "drop_waist";

type LengthKind =
  | "mini"
  | "knee"
  | "calf"
  | "ankle"
  | "floor"
  | "chapel"
  | "cathedral"
  | "royal";

type SleeveKind =
  | "sleeveless"
  | "short"
  | "elbow"
  | "three_quarter"
  | "long"
  | "bell"
  | "cutouts"
  | "asymmetric";

type NecklineKind =
  | "off_shoulder"
  | "plunge"
  | "v"
  | "sweetheart"
  | "scoop"
  | "draped"
  | "boat"
  | "straight"
  | "collared"
  | "halter"
  | "high"
  | "square";

type FabricKind =
  | "cotton"
  | "charmeuse"
  | "satin"
  | "mikado"
  | "organza"
  | "tulle"
  | "velvet"
  | "crepe"
  | "dupioni"
  | "lace"
  | "beaded"
  | "sequin";

type BackKind =
  | "low_v"
  | "corset"
  | "illusion"
  | "keyhole"
  | "crisscross"
  | "buttons"
  | "zip"
  | "bow"
  | "square";

type SpriteSheetId =
  | "silhouettes"
  | "lengths"
  | "sleeves"
  | "necklines"
  | "fabrics"
  | "backs";

interface SpriteSheet {
  src: string;
  cols: number;
  rows: number;
}

export interface SpriteRef {
  sheet: SpriteSheetId;
  index: number;
}

export type VisualDescriptor =
  | { kind: "color"; colors: string[]; colorLabel?: string }
  | { kind: "sprite"; sprite: SpriteRef }
  | {
      kind: "composite";
      sprites: SpriteRef[];
      colors: string[];
      colorLabel?: string;
    }
  | { kind: "upload" };

const CUSTOM_BUNDLE_FIELDS = [
  "color",
  "length",
  "sleeve",
  "neckline",
  "silhouette",
  "fabric",
] as const;

const SPRITE_SHEETS: Record<SpriteSheetId, SpriteSheet> = {
  silhouettes: {
    src: "/images/create-action-sprites/silhouettes.png",
    cols: 3,
    rows: 3,
  },
  lengths: {
    src: "/images/create-action-sprites/lengths.png",
    cols: 4,
    rows: 2,
  },
  sleeves: {
    src: "/images/create-action-sprites/sleeves.png",
    cols: 4,
    rows: 2,
  },
  necklines: {
    src: "/images/create-action-sprites/necklines.png",
    cols: 4,
    rows: 3,
  },
  fabrics: {
    src: "/images/create-action-sprites/fabrics.png",
    cols: 4,
    rows: 3,
  },
  backs: {
    src: "/images/create-action-sprites/backs.png",
    cols: 3,
    rows: 3,
  },
};

export const SPRITE_LABELS: Record<SpriteSheetId, string> = {
  silhouettes: "Shape",
  lengths: "Length",
  sleeves: "Sleeve",
  necklines: "Neck",
  fabrics: "Fabric",
  backs: "Back",
};

const SILHOUETTE_INDEX: Record<SilhouetteKind, number> = {
  ball_gown: 0,
  a_line: 1,
  mermaid: 2,
  trumpet: 3,
  fit_flare: 4,
  empire: 5,
  sheath: 6,
  fitted: 7,
  drop_waist: 8,
};

const LENGTH_INDEX: Record<LengthKind, number> = {
  mini: 0,
  knee: 1,
  calf: 2,
  ankle: 3,
  floor: 4,
  chapel: 5,
  cathedral: 6,
  royal: 7,
};

const SLEEVE_INDEX: Record<SleeveKind, number> = {
  sleeveless: 0,
  short: 1,
  elbow: 2,
  three_quarter: 3,
  long: 4,
  bell: 5,
  cutouts: 6,
  asymmetric: 7,
};

const NECKLINE_INDEX: Record<NecklineKind, number> = {
  off_shoulder: 0,
  plunge: 1,
  v: 2,
  sweetheart: 3,
  scoop: 4,
  draped: 5,
  boat: 6,
  straight: 7,
  collared: 8,
  halter: 9,
  high: 10,
  square: 11,
};

const FABRIC_INDEX: Record<FabricKind, number> = {
  cotton: 0,
  charmeuse: 1,
  satin: 2,
  mikado: 3,
  organza: 4,
  tulle: 5,
  velvet: 6,
  crepe: 7,
  dupioni: 8,
  lace: 9,
  beaded: 10,
  sequin: 11,
};

const BACK_INDEX: Record<BackKind, number> = {
  low_v: 0,
  corset: 1,
  illusion: 2,
  keyhole: 3,
  crisscross: 4,
  buttons: 5,
  zip: 6,
  bow: 7,
  square: 8,
};

function normalizeText(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .toLowerCase()
    .replace(/[–—]/g, "-")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getDataValue(
  preset: PresetOption,
  keys: string[]
): string | string[] | null {
  const data = preset.data as Record<string, unknown> | undefined;
  for (const key of keys) {
    const value = data?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
    if (Array.isArray(value)) {
      const strings = value.filter(
        item => typeof item === "string" && item.trim()
      ) as string[];
      if (strings.length > 0) return strings.map(item => item.trim());
    }
  }
  return null;
}

function joinVisualText(...values: unknown[]): string {
  return values
    .flatMap(value => (Array.isArray(value) ? value : [value]))
    .filter((value): value is string => typeof value === "string")
    .map(value => value.trim())
    .filter(Boolean)
    .join(" ");
}

function getColorLabel(value: unknown): string | undefined {
  if (Array.isArray(value)) {
    const label = value
      .filter((part): part is string => typeof part === "string")
      .map(part => part.trim())
      .filter(Boolean)
      .join(", ");
    return label || undefined;
  }

  if (typeof value !== "string") return undefined;
  const label = value.trim();
  return label || undefined;
}

function classifySilhouette(value: unknown): SilhouetteKind {
  const text = normalizeText(value);
  if (/ball|princess/.test(text)) return "ball_gown";
  if (/a line|aline/.test(text)) return "a_line";
  if (/mermaid/.test(text)) return "mermaid";
  if (/trumpet/.test(text)) return "trumpet";
  if (/fit.*flare|flare/.test(text)) return "fit_flare";
  if (/empire/.test(text)) return "empire";
  if (/sheath|column/.test(text)) return "sheath";
  if (/fitted|body hugging|tight/.test(text)) return "fitted";
  if (/drop.*waist|dropwaist/.test(text)) return "drop_waist";
  return "a_line";
}

function classifyLength(value: unknown): LengthKind | undefined {
  const text = normalizeText(value);
  if (/mini|above.*knee|short/.test(text)) return "mini";
  if (/knee/.test(text)) return "knee";
  if (/calf|midi|tea/.test(text)) return "calf";
  if (/ankle/.test(text)) return "ankle";
  if (/chapel/.test(text)) return "chapel";
  if (/cathedral/.test(text)) return "cathedral";
  if (/royal|dramatic.*train/.test(text)) return "royal";
  if (/floor|train/.test(text)) return "floor";
  return undefined;
}

function classifySleeve(value: unknown): SleeveKind | undefined {
  const text = Array.isArray(value)
    ? value.map(normalizeText).join(" ")
    : normalizeText(value);
  if (/asymmetric|one sleeve|remove.*sleeve|left|right/.test(text)) {
    return "asymmetric";
  }
  if (/cutout|open shoulder/.test(text)) return "cutouts";
  if (/sleeveless|strapless|no sleeve/.test(text)) return "sleeveless";
  if (/short|cap/.test(text)) return "short";
  if (/three|quarter|3 4/.test(text)) return "three_quarter";
  if (/elbow/.test(text)) return "elbow";
  if (/long|full/.test(text)) return "long";
  if (/bell/.test(text)) return "bell";
  return undefined;
}

function classifyNeckline(value: unknown): NecklineKind | undefined {
  const text = normalizeText(value);
  if (/off shoulder|offshoulder/.test(text)) return "off_shoulder";
  if (/plunge|deep/.test(text)) return "plunge";
  if (/\bv neck\b|\bv\b|classic v/.test(text)) return "v";
  if (/sweetheart|heart/.test(text)) return "sweetheart";
  if (/scoop|u shaped/.test(text)) return "scoop";
  if (/draped|drape/.test(text)) return "draped";
  if (/boat|bateau/.test(text)) return "boat";
  if (/straight|horizontal/.test(text)) return "straight";
  if (/collared|shirt/.test(text)) return "collared";
  if (/halter/.test(text)) return "halter";
  if (/high|conservative/.test(text)) return "high";
  if (/square/.test(text)) return "square";
  return undefined;
}

function classifyFabric(value: unknown): FabricKind | undefined {
  const text = normalizeText(value);
  if (/cotton/.test(text)) return "cotton";
  if (/charmeuse/.test(text)) return "charmeuse";
  if (/satin|duchess/.test(text)) return "satin";
  if (/mikado/.test(text)) return "mikado";
  if (/organza/.test(text)) return "organza";
  if (/tulle|netting/.test(text)) return "tulle";
  if (/velvet/.test(text)) return "velvet";
  if (/crepe/.test(text)) return "crepe";
  if (/dupioni|slub/.test(text)) return "dupioni";
  if (/bead|pearl|embroidery/.test(text)) return "beaded";
  if (/sequin|sparkle|shimmer/.test(text)) return "sequin";
  if (/lace|openwork/.test(text)) return "lace";
  return undefined;
}

function classifyBack(value: unknown): BackKind {
  const text = normalizeText(value);
  if (/low.*v|v back/.test(text)) return "low_v";
  if (/corset|lace up|boning/.test(text)) return "corset";
  if (/illusion|mesh|applique/.test(text)) return "illusion";
  if (/keyhole|circular/.test(text)) return "keyhole";
  if (/crisscross|cross strap/.test(text)) return "crisscross";
  if (/button/.test(text)) return "buttons";
  if (/zip|zipper/.test(text)) return "zip";
  if (/bow|tie/.test(text)) return "bow";
  if (/square/.test(text)) return "square";
  return "low_v";
}

function getVibeDefaults(value: unknown): {
  silhouette: SilhouetteKind;
  length: LengthKind;
  sleeve: SleeveKind;
  neckline: NecklineKind;
  fabric: FabricKind;
  colors: string[];
} {
  const text = normalizeText(value);
  if (
    /\b(?:sleek|minimal|minimalist|modern|sculpted|architectural|clean)\b/.test(
      text
    )
  ) {
    return {
      silhouette: "sheath",
      length: "floor",
      sleeve: "sleeveless",
      neckline: "straight",
      fabric: "crepe",
      colors: ["#F8F6F0", "#A8B8FF", "#111936"],
    };
  }
  if (
    /\b(?:dramatic|opulence|opulent|glam|entrance|bold|statement)\b/.test(text)
  ) {
    return {
      silhouette: "ball_gown",
      length: "cathedral",
      sleeve: "long",
      neckline: "plunge",
      fabric: "velvet",
      colors: ["#111936", "#800020", "#FFD700"],
    };
  }
  if (
    /\b(?:garden|airy|beach|coastal|lightweight|effortless|outdoor|summer)\b/.test(
      text
    )
  ) {
    return {
      silhouette: "empire",
      length: "ankle",
      sleeve: "short",
      neckline: "scoop",
      fabric: "organza",
      colors: ["#FFFFF0", "#9DC183", "#00BFC4"],
    };
  }
  if (
    /\b(?:modest|coverage|covered|supportive|conservative|bra friendly)\b/.test(
      text
    )
  ) {
    return {
      silhouette: "a_line",
      length: "floor",
      sleeve: "long",
      neckline: "high",
      fabric: "crepe",
      colors: ["#F7E7CE", "#8EA985", "#111936"],
    };
  }
  if (
    /\b(?:dance|move|movement|practical|guest|party|comfortable)\b/.test(text)
  ) {
    return {
      silhouette: "fit_flare",
      length: "calf",
      sleeve: "sleeveless",
      neckline: "v",
      fabric: "charmeuse",
      colors: ["#FFDAB9", "#0F52BA", "#FF7F50"],
    };
  }
  return {
    silhouette: "a_line",
    length: "chapel",
    sleeve: "three_quarter",
    neckline: "sweetheart",
    fabric: "lace",
    colors: ["#FFFFF0", "#FFE4E1", "#E6E6FA"],
  };
}

function silhouetteSprite(kind: SilhouetteKind): SpriteRef {
  return { sheet: "silhouettes", index: SILHOUETTE_INDEX[kind] };
}

function lengthSprite(kind: LengthKind): SpriteRef {
  return { sheet: "lengths", index: LENGTH_INDEX[kind] };
}

function sleeveSprite(kind: SleeveKind): SpriteRef {
  return { sheet: "sleeves", index: SLEEVE_INDEX[kind] };
}

function necklineSprite(kind: NecklineKind): SpriteRef {
  return { sheet: "necklines", index: NECKLINE_INDEX[kind] };
}

function fabricSprite(kind: FabricKind): SpriteRef {
  return { sheet: "fabrics", index: FABRIC_INDEX[kind] };
}

function backSprite(kind: BackKind): SpriteRef {
  return { sheet: "backs", index: BACK_INDEX[kind] };
}

function uniqueSprites(sprites: SpriteRef[]): SpriteRef[] {
  const seen = new Set<string>();
  return sprites.filter(sprite => {
    const key = `${sprite.sheet}:${sprite.index}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function getCustomSprites(
  label: string,
  data: Record<string, unknown> | undefined
): { sprites: SpriteRef[]; colors: string[]; colorLabel?: string } | null {
  const visualText = joinVisualText(
    label,
    data?.prompt,
    data?.color,
    data?.length,
    data?.sleeve,
    data?.sleeveAddons,
    data?.neckline,
    data?.silhouette,
    data?.fabric
  );
  const defaults = getVibeDefaults(visualText);
  const explicitColors = getColorHexesForValue(data?.color, data?.colorHex);
  const explicitColorLabel = getColorLabel(data?.color);
  const silhouette =
    data?.silhouette !== undefined
      ? classifySilhouette(data.silhouette)
      : defaults.silhouette;
  const fabric =
    (data?.fabric !== undefined
      ? classifyFabric(data.fabric)
      : classifyFabric(visualText)) ?? defaults.fabric;
  const length =
    (data?.length !== undefined
      ? classifyLength(data.length)
      : classifyLength(visualText)) ?? defaults.length;
  const neckline =
    (data?.neckline !== undefined
      ? classifyNeckline(data.neckline)
      : classifyNeckline(visualText)) ?? defaults.neckline;
  const sleeve =
    (data?.sleeve !== undefined || data?.sleeveAddons !== undefined
      ? classifySleeve(data.sleeveAddons ?? data.sleeve)
      : classifySleeve(visualText)) ?? defaults.sleeve;
  const designFieldCount = CUSTOM_BUNDLE_FIELDS.filter(
    field => data?.[field] !== undefined
  ).length;
  const isBundledCustomPreset = designFieldCount >= 3;

  if (!isBundledCustomPreset) {
    const explicitSprites: SpriteRef[] = [];
    const explicitLength =
      data?.length !== undefined ? classifyLength(data.length) : undefined;
    const explicitNeckline =
      data?.neckline !== undefined
        ? classifyNeckline(data.neckline)
        : undefined;
    const explicitSleeve =
      data?.sleeve !== undefined || data?.sleeveAddons !== undefined
        ? classifySleeve(data.sleeveAddons ?? data.sleeve)
        : undefined;

    if (data?.silhouette !== undefined) {
      explicitSprites.push(silhouetteSprite(silhouette));
    }
    if (explicitLength) {
      explicitSprites.push(lengthSprite(explicitLength));
    }
    if (explicitNeckline) {
      explicitSprites.push(necklineSprite(explicitNeckline));
    }
    if (explicitSleeve) {
      explicitSprites.push(sleeveSprite(explicitSleeve));
    }
    if (data?.fabric !== undefined) {
      explicitSprites.push(fabricSprite(fabric));
    }

    const sprites = uniqueSprites(explicitSprites);
    if (sprites.length === 0 && explicitColors.length === 0) {
      return null;
    }

    return {
      colors: explicitColors,
      colorLabel: explicitColorLabel,
      sprites,
    };
  }

  const textColors = getColorHexesForValue(visualText);
  const colors =
    explicitColorLabel || data?.colorHex !== undefined
      ? explicitColors
      : textColors.length > 0
        ? textColors
        : defaults.colors;

  return {
    colors,
    colorLabel: explicitColors.length > 0 ? explicitColorLabel : undefined,
    sprites: uniqueSprites([
      silhouetteSprite(silhouette),
      lengthSprite(length),
      necklineSprite(neckline),
      sleeveSprite(sleeve),
      fabricSprite(fabric),
    ]),
  };
}

export function getPresetDescriptor(
  preset: PresetOption
): VisualDescriptor | null {
  const label = preset.label;
  const data = preset.data as Record<string, unknown> | undefined;
  const colorValue = getDataValue(preset, ["color"]) ?? label;
  const colorHexValue = getDataValue(preset, ["colorHex", "color_hex"]);
  const colors = getColorHexesForSwatch(colorValue, colorHexValue);
  const colorLabel = getColorLabel(colorValue);

  if (
    preset.actionId === "set_color" ||
    preset.actionData?.suggestedField === "color"
  ) {
    return colors.length > 0 ? { kind: "color", colors, colorLabel } : null;
  }

  if (preset.actionId === "set_fabric") {
    return {
      kind: "sprite",
      sprite: fabricSprite(classifyFabric(data?.fabric ?? label) ?? "satin"),
    };
  }

  if (preset.actionId === "set_silhouette") {
    return {
      kind: "sprite",
      sprite: silhouetteSprite(classifySilhouette(data?.silhouette ?? label)),
    };
  }

  if (preset.actionId === "set_length") {
    return {
      kind: "sprite",
      sprite: lengthSprite(classifyLength(data?.length ?? label) ?? "floor"),
    };
  }

  if (preset.actionId === "set_sleeve") {
    return {
      kind: "sprite",
      sprite: sleeveSprite(
        classifySleeve(data?.sleeve ?? data?.sleeveAddons ?? label) ??
          "sleeveless"
      ),
    };
  }

  if (preset.actionId === "set_neckline") {
    return {
      kind: "sprite",
      sprite: necklineSprite(
        classifyNeckline(data?.neckline ?? label) ?? "scoop"
      ),
    };
  }

  if (
    preset.actionId === "set_back_prompt" ||
    preset.actionId === "set_back_style"
  ) {
    return {
      kind: "sprite",
      sprite: backSprite(
        classifyBack(data?.backPrompt ?? data?.prompt ?? label)
      ),
    };
  }

  if (
    preset.actionId === "suggest_field" &&
    (preset.actionData?.suggestedField === "upload" ||
      preset.actionData?.suggestedField === "image_upload" ||
      preset.actionData?.suggestedField === "front_upload" ||
      preset.actionData?.suggestedField === "back_upload")
  ) {
    return { kind: "upload" };
  }

  if (preset.actionId === "custom") {
    if (isRefinementPreset(preset)) return null;
    const customVisual = getCustomSprites(label, data);
    if (!customVisual) return null;
    if (customVisual.sprites.length === 0 && customVisual.colors.length > 0) {
      return {
        kind: "color",
        colors: customVisual.colors,
        colorLabel: customVisual.colorLabel,
      };
    }
    if (customVisual.sprites.length === 1 && customVisual.colors.length === 0) {
      return { kind: "sprite", sprite: customVisual.sprites[0] };
    }
    return {
      kind: "composite",
      sprites: customVisual.sprites,
      colors: customVisual.colors,
      colorLabel: customVisual.colorLabel,
    };
  }

  return null;
}

export function getSpriteStyle(sprite: SpriteRef): React.CSSProperties {
  const sheet = SPRITE_SHEETS[sprite.sheet];
  const col = sprite.index % sheet.cols;
  const row = Math.floor(sprite.index / sheet.cols);
  const x = sheet.cols === 1 ? 0 : (col / (sheet.cols - 1)) * 100;
  const y = sheet.rows === 1 ? 0 : (row / (sheet.rows - 1)) * 100;

  return {
    backgroundImage: `url(${sheet.src})`,
    backgroundSize: `${sheet.cols * 100}% ${sheet.rows * 100}%`,
    backgroundPosition: `${x}% ${y}%`,
    backgroundRepeat: "no-repeat",
  };
}
